// Frame membership. A frame lists its children by id (spec §4.2); an object
// belongs to the topmost frame containing its centre. Frames don't nest.
// These functions compute the ops that bring children lists up to date after
// local edits; the editor applies them inside the same undo step.
import type { BoardObject, Frame, Op } from '../../shared/types.ts';
import type { Store } from './store.ts';
import type { SpatialIndex } from '../engine/spatial-index.ts';
import type { Rect } from '../util/geometry.ts';

const contains = (r: Rect, x: number, y: number) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;

export function framesIn(store: Store): Frame[] {
  const out: Frame[] = [];
  for (const o of store.values()) if (o.type === 'frame') out.push(o);
  return out;
}

/**
 * The topmost frame whose box contains the centre of `bounds`. Columns
 * (spec §13.6) only take cards: anything else over one stays free.
 */
function frameFor(o: BoardObject, bounds: Rect, frames: readonly Frame[], store: Store): Frame | null {
  const cx = bounds.x + bounds.w / 2, cy = bounds.y + bounds.h / 2;
  let best: Frame | null = null;
  for (const f of frames) {
    if (f.layout === 'column' && o.type !== 'card') continue;
    if (contains(f, cx, cy) && (!best || store.rank(f.id) > store.rank(best.id))) best = f;
  }
  return best;
}

/**
 * Ops that make frame children lists correct for `touched` objects (moved,
 * created, resized) and for any frames among them (whose area changed).
 */
export function membershipOps(
  store: Store, index: SpatialIndex, frames: readonly Frame[], touched: Iterable<string>, boundsOf: (o: BoardObject) => Rect,
): Op[] {
  if (!frames.length) return [];
  const parent = new Map<string, string>();
  const next = new Map<string, string[]>(); // frame id → working copy of children
  for (const f of frames) for (const c of f.children) parent.set(c, f.id);

  const candidates = new Set<string>();
  for (const id of touched) {
    const o = store.get(id);
    if (!o) continue;
    if (o.type !== 'frame') {
      candidates.add(id);
      continue;
    }
    // A frame that moved or resized: re-examine everything it covers or covered.
    for (const c of o.children) candidates.add(c);
    for (const c of index.query(o)) candidates.add(c);
  }

  const childrenOf = (fid: string) => {
    let list = next.get(fid);
    if (!list) next.set(fid, (list = [...(store.get(fid) as Frame).children]));
    return list;
  };
  for (const id of candidates) {
    const o = store.get(id);
    // Connectors follow their endpoints rather than belonging to frames.
    if (!o || o.type === 'frame' || o.type === 'connector') continue;
    const want = frameFor(o, boundsOf(o), frames, store)?.id ?? null;
    const have = parent.get(id) ?? null;
    if (want === have) continue;
    if (have && store.has(have)) {
      const list = childrenOf(have);
      list.splice(list.indexOf(id), 1);
    }
    if (want) childrenOf(want).push(id);
  }

  const ops: Op[] = [];
  for (const [fid, children] of next) ops.push({ t: 'update', id: fid, props: { children } });
  return ops;
}

/** Objects plus the children of any frames among them (moving a frame moves its contents). */
export function withFrameChildren(store: Store, objs: readonly BoardObject[]): BoardObject[] {
  const seen = new Set(objs.map((o) => o.id));
  const out = [...objs];
  for (const o of objs) {
    if (o.type !== 'frame') continue;
    for (const id of o.children) {
      const child = store.get(id);
      if (child && !seen.has(id)) {
        seen.add(id);
        out.push(child);
      }
    }
  }
  return out;
}
